<template>
  <aside
    class="fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 shadow-xl transform transition-all duration-300 ease-in-out lg:translate-x-0"
    :class="[
      { '-translate-x-full': !isOpen, 'translate-x-0': isOpen },
      { 'w-64': !isCollapsed, 'w-64 lg:w-20': isCollapsed }
    ]"
  >
    <div class="flex flex-col h-full">
      <!-- Logo Section -->
      <div class="flex items-center justify-center h-16 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-center space-x-2 px-2 w-full transition-all duration-300">
          <div class="h-8 flex items-center justify-center flex-shrink-0">
            <!-- Light mode logos -->
            <img 
              v-if="!isCollapsed"
              src="@/assets/main-logo1x1.png" 
              alt="Logo" 
              class="h-10 transition-all duration-300 dark:hidden"
            />
            <img 
              v-else
              src="@/assets/main-logo1x1.png" 
              alt="Logo" 
              class="h-10 lg:hidden transition-all duration-300 dark:hidden"
            />
            <img 
              v-if="isCollapsed"
              src="@/assets/comp-logo.png" 
              alt="Logo" 
              class="h-8 hidden lg:block transition-all duration-300 dark:hidden"
            />
            
            <!-- Dark mode logos -->
            <img 
              v-if="!isCollapsed"
              src="@/assets/main-logo1x1-dark.png" 
              alt="Logo" 
              class="h-10 transition-all duration-300 hidden dark:block"
            />
            <img 
              v-if="isCollapsed"
              src="@/assets/comp-logo.png" 
              alt="Logo" 
              class="h-8 hidden lg:dark:block transition-all duration-300"
            />
          </div>
        </div>
      </div>

      <!-- Navigation Section -->
      <nav class="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        <router-link
          v-for="item in navigation"
          :key="item.to"
          :to="item.to"
          class="group flex items-center py-2.5 text-primary-text dark:text-primary-dark-text rounded-lg transition-all duration-200 relative overflow-hidden"
          :class="[
            {
              'bg-gradient-to-r from-primary-bg to-primary-bg/10 dark:from-primary-dark/20 dark:to-primary-dark/50 text-primary dark:text-primary-dark': isActive(item.to),
              'hover:bg-primary-bg dark:hover:bg-primary-dark-bg/20': !isActive(item.to),
              'lg-tooltip': isCollapsed
            },
            isCollapsed ? 'px-2 lg:justify-center' : 'px-3',
            isCollapsed ? 'justify-start lg:justify-center' : 'justify-start'
          ]"
          :title="isCollapsed ? item.name : ''"
        >
          <!-- Active Indicator -->
          <div
            v-if="isActive(item.to)"
            class="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-primary-hover dark:from-primary-dark dark:to-sec-dark-hover rounded-r-full"
          ></div>

          <!-- Navigation Item Content -->
          <div 
            class="flex items-center transition-all duration-200 relative z-10"
            :class="{
              'lg:justify-center space-x-3 lg:space-x-0': isCollapsed,
              'space-x-3': !isCollapsed
            }"
          >
            <div
              class="flex items-center justify-center rounded-lg transition-all duration-200"
              :class="[
                {
                  'bg-primary-bg/20 dark:bg-primary-dark/20': isActive(item.to),
                  'bg-primary-bg/50 dark:bg-primary-dark-bg/50': !isActive(item.to)
                },
                isCollapsed ? 'w-10 h-10' : 'w-8 h-8'
              ]"
            >
              <component 
                :is="item.icon" 
                :class="[
                  'w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:scale-110',
                  isActive(item.to) 
                    ? 'text-primary dark:text-primary-dark' 
                    : 'text-primary-text/70 dark:text-primary-dark-text/70 group-hover:text-primary/80 dark:group-hover:text-primary-dark/80'
                ]"
              />
            </div>
            <span 
              class="text-sm font-bold truncate transition-all duration-200"
              :class="{
                'lg:hidden': isCollapsed,
                'block': !isCollapsed
              }"
            >
              {{ item.name }}
            </span>
          </div>

          <!-- Hover Effect -->
          <div
            class="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 dark:from-primary-dark/5 dark:via-primary-dark/10 dark:to-primary-dark/5 transition-all duration-300"
            :class="{ 'opacity-0': isActive(item.to) }"
          ></div>
        </router-link>
      </nav>

      <!-- Footer Section -->
      <div v-if="!isCollapsed" class="p-4 border-t border-gray-200 dark:border-gray-700 transition-all duration-300">
        <div class="flex items-center justify-center space-x-2 text-xs text-primary-text dark:text-primary-dark-text">
          <span>© {{ new Date().getFullYear() }}</span>
          <span class="w-1 h-1 rounded-full bg-primary-bg dark:bg-primary-dark-bg"></span>
          <span>Developed by Proweaver</span>
        </div>
      </div>
    </div>
  </aside>

  <!-- Mobile Overlay -->
  <div
    v-if="isOpen"
    class="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity duration-300"
    @click="toggleSidebar"
  ></div>
</template>

<script setup lang="ts">
// Imports
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getNavigationItems } from '@/router'
import { useUser } from '@/composables/auth/useUser'

// Props & Emits
const props = defineProps<{
  isOpen: boolean
  isCollapsed: boolean
}>()

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
  (e: 'update:isCollapsed', value: boolean): void
}>()

// Composables
const route = useRoute()
const router = useRouter()
const { userType } = useUser()

// Computed Properties
const navigation = computed(() => getNavigationItems([...router.options.routes], userType.value))

// Methods
const isActive = (path: string) => route.path === path
const toggleSidebar = () => emit('update:isOpen', !props.isOpen)
</script>

<style scoped>
/* Enhanced transition animations */
.transition-all {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Smooth width transitions */
aside {
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Enhanced hover effects for collapsed sidebar */
.group:hover {
  transform: translateY(-1px);
}

/* Icon scaling animation */
.group-hover\:scale-110:hover {
  transform: scale(1.1);
}

/* Tooltip enhancement - only show on desktop */
.lg-tooltip:hover::after {
  position: absolute;
  z-index: 1000;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 0.5rem;
  padding: 0.25rem 0.5rem;
  background-color: #1f2937;
  color: white;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  white-space: nowrap;
  content: attr(title);
  opacity: 0.9;
  pointer-events: none;
}

@media (max-width: 1024px) {
  .lg-tooltip:hover::after {
    display: none;
  }
}

/* Dark mode tooltip */
.dark .lg-tooltip:hover::after {
  background-color: #374151;
}
</style> 